import React, { useState, useEffect } from 'react';
import { FiPlus, FiX, FiCheck, FiCopy as Copy, FiSave, FiLink, FiTag, FiLayers, FiSearch } from 'react-icons/fi';
import { Card, CardHeader, CardTitle, CardContent } from '../../../../components/ui/Card';
import TagPairEditor from '../../workloads/components/TagPairEditor';
import WorkloadModal from '../../workloads/components/WorkloadModal';
import { useWorkloads } from '../../workloads/hooks/useWorkloads';
import { useFilter } from '../../../../contexts/FilterContext';
import { workloadsApi } from '../../../../services/workloadsApi';
import { apiCall } from '../../../../services/realApi';
import { projectAPI } from '../../../../services/api';
import { toast } from 'react-hot-toast';
import type { WorkloadSummary, WorkloadCreate, WorkloadTagPair } from '../../../../types/workload';

interface ProjectTaggingSectionProps {
  project: {
    id: string;
    name: string;
    costTagKey?: string | null;
    costTagValue?: string | null;
    costTrackingEnabled?: boolean;
  };
  onRefresh?: () => void;
}

const ProjectTaggingSection: React.FC<ProjectTaggingSectionProps> = ({ project, onRefresh }) => {
  const { workloads, refresh: refreshWorkloads } = useWorkloads();
  const { accounts, environments } = useFilter();

  const [costTrackingEnabled, setCostTrackingEnabled] = useState(project.costTrackingEnabled ?? false);
  const [costTrackingLoading, setCostTrackingLoading] = useState(false);
  const [linkedWorkloads, setLinkedWorkloads] = useState<WorkloadSummary[]>([]);

  // Project tag pairs
  const [projectTagPairs, setProjectTagPairs] = useState<WorkloadTagPair[]>([]);
  const [editingProjectTags, setEditingProjectTags] = useState<WorkloadTagPair[]>([]);
  const [isEditingProjectTags, setIsEditingProjectTags] = useState(false);
  const [isSavingProjectTags, setIsSavingProjectTags] = useState(false);

  // Workload association
  const [showLinkSearch, setShowLinkSearch] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isWorkloadModalOpen, setIsWorkloadModalOpen] = useState(false);

  // Clipboard
  const [copiedField, setCopiedField] = useState<string | null>(null);
  const [showCostTrackingHelp, setShowCostTrackingHelp] = useState(false);

  useEffect(() => {
    setCostTrackingEnabled(project.costTrackingEnabled ?? false);
  }, [project.costTrackingEnabled]);

  useEffect(() => {
    loadLinkedWorkloads();
    loadProjectTagPairs();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [project.id]);

  const handleToggleCostTracking = async (enabled: boolean) => {
    setCostTrackingLoading(true);
    try {
      const result = await projectAPI.updateCostConfig(project.id, { cost_tracking_enabled: enabled });
      if (result.success) {
        setCostTrackingEnabled(enabled);
        toast.success(`Cost tracking ${enabled ? 'enabled' : 'disabled'}`);
        onRefresh?.();
      } else {
        toast.error('Failed to update cost tracking: ' + result.error);
      }
    } catch {
      toast.error('Failed to update cost tracking');
    } finally {
      setCostTrackingLoading(false);
    }
  };

  const loadLinkedWorkloads = async () => {
    try {
      const data = await workloadsApi.getForProject(project.id);
      setLinkedWorkloads(Array.isArray(data) ? data : []);
    } catch { setLinkedWorkloads([]); }
  };

  const loadProjectTagPairs = async () => {
    try {
      const data = await apiCall(`/projects/${project.id}/tag-pairs`);
      setProjectTagPairs(Array.isArray(data) ? data : []);
    } catch { setProjectTagPairs([]); }
  };

  const handleCopy = (text: string, field: string) => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);
    setTimeout(() => setCopiedField(null), 2000);
  };

  const handleSaveProjectTags = async () => {
    const validPairs = editingProjectTags.filter(p => p.tag_key.trim() && p.tag_value.trim());
    if (validPairs.length === 0) {
      toast.error('At least one tag pair with key and value is required');
      return;
    }
    setIsSavingProjectTags(true);
    try {
      await apiCall(`/projects/${project.id}/tag-pairs`, {
        method: 'PUT',
        body: JSON.stringify({ tag_pairs: validPairs }),
      });
      toast.success('Project tags saved');
      setProjectTagPairs(validPairs);
      setIsEditingProjectTags(false);
      onRefresh?.();
    } catch { toast.error('Failed to save project tags'); }
    finally { setIsSavingProjectTags(false); }
  };

  const handleRemoveProjectTags = async () => {
    try {
      await apiCall(`/projects/${project.id}/tag-pairs`, {
        method: 'PUT',
        body: JSON.stringify({ tag_pairs: [] }),
      });
      setProjectTagPairs([]);
      toast.success('Project tags removed');
      onRefresh?.();
    } catch { toast.error('Failed to remove project tags'); }
  };

  const startEditingProjectTags = () => {
    setEditingProjectTags(
      projectTagPairs.length > 0 ? [...projectTagPairs] : [{ tag_key: '', tag_value: '', operator: null }]
    );
    setIsEditingProjectTags(true);
  };

  const handleLinkWorkload = async (workload: WorkloadSummary) => {
    try {
      await workloadsApi.linkProject(workload.id, project.id);
      await loadLinkedWorkloads();
      await refreshWorkloads();
      setShowLinkSearch(false);
      setSearchQuery('');
      toast.success(`Linked "${workload.name}"`);
    } catch { toast.error('Failed to link workload'); }
  };

  const handleUnlinkWorkload = async (workload: WorkloadSummary) => {
    try {
      await workloadsApi.unlinkProject(workload.id, project.id);
      await loadLinkedWorkloads();
      await refreshWorkloads();
    } catch { toast.error('Failed to unlink workload'); }
  };

  const handleCreateWorkloadInline = async (data: WorkloadCreate) => {
    await workloadsApi.create({ ...data, project_id: project.id });
    await loadLinkedWorkloads();
    await refreshWorkloads();
    toast.success('Workload created and linked');
  };

  const availableWorkloads = workloads.filter((w) => !linkedWorkloads.some((lw) => lw.id === w.id));
  const filteredAvailable = searchQuery
    ? availableWorkloads.filter((w) => w.name.toLowerCase().includes(searchQuery.toLowerCase()))
    : availableWorkloads;

  const primaryTag = projectTagPairs.length > 0 ? projectTagPairs[0] : null;

  // Determine which section is active (has data)
  const hasProjectTags = projectTagPairs.length > 0 || isEditingProjectTags;
  const hasWorkloads = linkedWorkloads.length > 0;
  // If one has data, the other is "inactive" (grayed out)
  const projectTagsInactive = !hasProjectTags && hasWorkloads;
  const workloadsInactive = !hasWorkloads && hasProjectTags;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Tagging</CardTitle>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Tag this project with custom tags for cost tracking, or link workloads for infrastructure grouping.
        </p>
      </CardHeader>
      <CardContent>
        {/* ===== Cost Tracking Toggle ===== */}
        <div className="mb-4 pb-4 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center justify-between">
            <div>
              <label htmlFor="cost-tracking-toggle" className="text-sm font-medium text-gray-900 dark:text-white">
                Enable Cost Tracking
              </label>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                Track infrastructure costs using the tags configured below.{' '}
                <button
                  type="button"
                  onClick={() => setShowCostTrackingHelp(prev => !prev)}
                  className="text-blue-500 hover:text-blue-600 dark:text-blue-400 dark:hover:text-blue-300 underline"
                >
                  Learn more
                </button>
              </p>
            </div>
            <button
              id="cost-tracking-toggle"
              type="button"
              role="switch"
              aria-checked={costTrackingEnabled}
              disabled={costTrackingLoading}
              onClick={() => handleToggleCostTracking(!costTrackingEnabled)}
              className={`relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 ${
                costTrackingEnabled ? 'bg-green-500' : 'bg-gray-300 dark:bg-gray-600'
              }`}
            >
              <span
                className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                  costTrackingEnabled ? 'translate-x-5' : 'translate-x-0'
                }`}
              />
            </button>
          </div>
          {showCostTrackingHelp && (
            <div className="mt-3 p-3 bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-md text-xs text-gray-700 dark:text-gray-300 space-y-2">
              <p className="font-medium text-blue-800 dark:text-blue-200">How cost tracking works:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Aivilo Lens matches the tags configured below against AWS resource tags to allocate infrastructure costs to this project.</li>
                <li><strong>AWS Cost Allocation Tags:</strong> For costs to appear in Cost Explorer breakdowns, the tag key used here must be activated as a <strong>Cost Allocation Tag</strong> in your AWS Billing console.</li>
                <li><strong>Project tags vs Linked workloads:</strong> You can define tags directly on this project, or link workloads that already have tag pairs configured. Both approaches work for cost tracking.</li>
                <li><strong>AND/OR logic:</strong> When using AND, a resource must have ALL tags in the group. When using OR, matching ANY group is sufficient. Cost allocation uses the <strong>primary tag key</strong> (the most common one across your tags).</li>
              </ul>
            </div>
          )}
        </div>

        {/* ===== Project Tags ===== */}
        <div className={`mb-0 ${projectTagsInactive ? 'opacity-50' : ''}`}>
          <div className="flex items-center gap-2 mb-2">
            <FiTag className="w-3.5 h-3.5 text-green-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Project Tags</h4>
          </div>

          {isEditingProjectTags ? (
            <div className="space-y-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <TagPairEditor tagPairs={editingProjectTags} onChange={setEditingProjectTags} />
              <div className="flex items-center gap-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                <button
                  onClick={handleSaveProjectTags}
                  disabled={isSavingProjectTags}
                  className="flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-md hover:bg-blue-700 disabled:opacity-50"
                >
                  <FiSave className="w-3.5 h-3.5" />
                  {isSavingProjectTags ? 'Saving...' : 'Save Tags'}
                </button>
                <button
                  onClick={() => setIsEditingProjectTags(false)}
                  className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                >
                  Cancel
                </button>
              </div>
            </div>
          ) : projectTagPairs.length > 0 ? (
            <div className="space-y-2">
              <div className="flex items-start justify-between gap-3 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                <div className="flex flex-wrap items-center gap-1.5">
                  {projectTagPairs.map((tp, i) => (
                    <React.Fragment key={i}>
                      <code className="text-sm font-mono text-gray-900 dark:text-white bg-gray-100 dark:bg-gray-700 px-2 py-0.5 rounded">
                        {tp.tag_key}={tp.tag_value}
                      </code>
                      {i < projectTagPairs.length - 1 && tp.operator && (
                        <span className={`text-xs font-semibold ${
                          tp.operator === 'OR' ? 'text-orange-600 dark:text-orange-400' : 'text-blue-600 dark:text-blue-400'
                        }`}>{tp.operator}</span>
                      )}
                    </React.Fragment>
                  ))}
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button onClick={startEditingProjectTags} className="text-xs text-blue-600 dark:text-blue-400 hover:underline">
                    Edit
                  </button>
                  <button onClick={handleRemoveProjectTags} className="text-xs text-red-500 dark:text-red-400 hover:underline">
                    Remove
                  </button>
                </div>
              </div>
              {primaryTag && (
                <div className="flex items-center justify-between p-2.5 bg-gray-50 dark:bg-gray-800/50 rounded-md">
                  <div className="flex items-center gap-2 min-w-0">
                    <span className="text-xs font-medium text-gray-500 dark:text-gray-400 shrink-0">SDK</span>
                    <code className="text-sm font-mono text-gray-900 dark:text-white truncate">
                      tracer.set_project(&quot;{primaryTag.tag_value}&quot;)
                    </code>
                  </div>
                  <button
                    onClick={() => handleCopy(`tracer.set_project("${primaryTag.tag_value}")`, 'sdk-project')}
                    className="ml-3 p-1.5 text-gray-400 hover:text-blue-600 transition-colors shrink-0"
                  >
                    {copiedField === 'sdk-project' ? <FiCheck className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
                  </button>
                </div>
              )}
            </div>
          ) : projectTagsInactive ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic py-2">
              Using workloads for tagging. Remove linked workloads to use project tags instead.
            </p>
          ) : (
            <button
              onClick={startEditingProjectTags}
              className="w-full flex items-center justify-center gap-1.5 px-4 py-3 text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors"
            >
              <FiPlus className="w-3.5 h-3.5" />
              Define project tags
            </button>
          )}
        </div>

        {/* OR Divider */}
        <div className="flex items-center gap-3 my-4">
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
          <span className="text-xs font-medium text-gray-400 dark:text-gray-500 uppercase">or</span>
          <div className="flex-1 border-t border-gray-200 dark:border-gray-700" />
        </div>

        {/* ===== Workloads ===== */}
        <div className={workloadsInactive ? 'opacity-50' : ''}>
          <div className="flex items-center gap-2 mb-2">
            <FiLayers className="w-3.5 h-3.5 text-purple-500" />
            <h4 className="text-sm font-semibold text-gray-900 dark:text-white">Linked Workloads</h4>
          </div>

          {linkedWorkloads.length > 0 ? (
            <div className="space-y-2">
              {linkedWorkloads.map((wl) => (
                <div key={wl.id} className="flex items-center justify-between p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
                  <div className="min-w-0">
                    <span className="text-sm font-medium text-gray-900 dark:text-white">{wl.name}</span>
                    <div className="flex flex-wrap items-center gap-1 mt-0.5">
                      {wl.tag_pairs.map((tp, i) => (
                        <React.Fragment key={i}>
                          <code className="text-xs font-mono text-gray-500 dark:text-gray-400">
                            {tp.tag_key}={tp.tag_value}
                          </code>
                          {i < wl.tag_pairs.length - 1 && tp.operator && (
                            <span className="text-xs text-gray-400">{tp.operator}</span>
                          )}
                        </React.Fragment>
                      ))}
                    </div>
                  </div>
                  <button onClick={() => handleUnlinkWorkload(wl)} className="p-1.5 text-gray-400 hover:text-red-500 transition-colors shrink-0" title="Unlink">
                    <FiX className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>
          ) : null}

          {workloadsInactive ? (
            <p className="text-xs text-gray-400 dark:text-gray-500 italic py-2">
              Using project tags for tagging. Remove project tags to use workloads instead.
            </p>
          ) : (
          <>
          {/* Action buttons */}
          <div className={`flex items-center gap-2 ${linkedWorkloads.length > 0 ? 'mt-2' : ''}`}>
            <button
              onClick={() => setShowLinkSearch(!showLinkSearch)}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-blue-400 hover:text-blue-600 dark:hover:border-blue-500 dark:hover:text-blue-400 transition-colors flex-1"
            >
              <FiLink className="w-3.5 h-3.5" />
              Link Existing
            </button>
            <button
              onClick={() => setIsWorkloadModalOpen(true)}
              className="flex items-center justify-center gap-1.5 px-4 py-2.5 text-sm text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-600 rounded-lg hover:border-purple-400 hover:text-purple-600 dark:hover:border-purple-500 dark:hover:text-purple-400 transition-colors flex-1"
            >
              <FiPlus className="w-3.5 h-3.5" />
              Create New
            </button>
          </div>

          {/* Link search */}
          {showLinkSearch && (
            <div className="mt-2 p-3 border border-gray-200 dark:border-gray-700 rounded-lg">
              <div className="relative mb-2">
                <FiSearch className="absolute left-2.5 top-2.5 w-4 h-4 text-gray-400" />
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search workloads..."
                  className="w-full pl-8 pr-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500"
                  autoFocus
                />
              </div>
              {filteredAvailable.length === 0 ? (
                <p className="text-sm text-gray-500 dark:text-gray-400 py-2 text-center">
                  {availableWorkloads.length === 0 ? 'No workloads available. Create one first.' : 'No matches.'}
                </p>
              ) : (
                <div className="space-y-1 max-h-40 overflow-y-auto">
                  {filteredAvailable.map((wl) => (
                    <button
                      key={wl.id}
                      onClick={() => handleLinkWorkload(wl)}
                      className="w-full flex items-center justify-between p-2 rounded-md text-left hover:bg-gray-50 dark:hover:bg-gray-700/50"
                    >
                      <div className="min-w-0">
                        <span className="text-sm font-medium text-gray-900 dark:text-white">{wl.name}</span>
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400 font-mono">
                          {wl.tag_pairs.map((tp) => `${tp.tag_key}=${tp.tag_value}`).join(', ')}
                        </span>
                      </div>
                      <FiPlus className="w-4 h-4 text-blue-500 shrink-0" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
          </>
          )}
        </div>

        <WorkloadModal
          isOpen={isWorkloadModalOpen}
          onClose={() => setIsWorkloadModalOpen(false)}
          onSave={async (data) => { await handleCreateWorkloadInline(data as WorkloadCreate); }}
          accounts={accounts}
          environments={environments}
          projectId={project.id}
        />
      </CardContent>
    </Card>
  );
};

export default ProjectTaggingSection;
